<template>
  <div class="member-user-menu-item-clipboard-copy">
    <a class="item" v-on:click="$_clipboardCopyClick">
      {{
        $t('appMemberUserMenuItemClipboardCopy.copyList', {
          maxCount: this.maxCopyCount.toLocaleString(),
        })
      }}
    </a>
  </div>
</template>

<script>
import { toRefs, computed } from 'vue'
import { ITEM_TYPE } from '@/composables/member/UseUserStatusAdditionalItems'
import StringUtil from '@/js/util/StringUtil'
import {
  useUserStatusAdditionalItemAccuracy,
  useUserStatusAdditionalItemActivity,
  useUserStatusAdditionalItemBattery,
  useUserStatusAdditionalItemIndoor,
  useUserStatusAdditionalItemPlace,
  useUserStatusAdditionalItemSensorData,
  useUserStatusAdditionalItemStatus,
  useUserStatusAdditionalItemUserAttribute,
  useUserStatusAdditionalItemPositioning,
  useUserStatusAdditionalItemRegistration,
} from '@/composables/user/additionalItem'

// item_type と使用 composable の対応 Map
// (device / sensor_names は専用の composable が無いため持たない。formatUserData 側で個別に扱う)
const ITEM_TYPE_COMPOSABLE_MAP = new Map([
  [ITEM_TYPE.ACCURACY, useUserStatusAdditionalItemAccuracy],
  [ITEM_TYPE.ACCURACY_RATING, useUserStatusAdditionalItemAccuracy],
  [ITEM_TYPE.ACTIVITY, useUserStatusAdditionalItemActivity],
  [ITEM_TYPE.ATTRIBUTE, useUserStatusAdditionalItemUserAttribute],
  [ITEM_TYPE.BATTERY, useUserStatusAdditionalItemBattery],
  [ITEM_TYPE.INDOOR, useUserStatusAdditionalItemIndoor],
  [ITEM_TYPE.PLACE, useUserStatusAdditionalItemPlace],
  [ITEM_TYPE.SENSOR_DATA, useUserStatusAdditionalItemSensorData],
  [ITEM_TYPE.STATUS, useUserStatusAdditionalItemStatus],
  [ITEM_TYPE.POSITIONING, useUserStatusAdditionalItemPositioning],
  [ITEM_TYPE.REGISTRATION, useUserStatusAdditionalItemRegistration],
])

export default {
  name: 'AppMemberUserMenuItemClipboardCopy',
  props: {
    /**
     * 表示中ユーザーリストデータ
     */
    users: {
      type: Array,
      default: () => [],
    },
    /**
     * 相対時間を計算する際の基準時刻
     */
    baseDate: {
      type: Date,
      default: null,
    },
    /**
     * コピー件数の上限
     */
    maxCopyCount: {
      type: Number,
      default: 1000,
    },
  },
  setup(props) {
    const { users, baseDate } = toRefs(props)

    /**
     * 各ユーザーのデータをコピー用にフォーマットする
     * @param {Object} user ユーザーデータ
     * @returns {string} フォーマット済みユーザーデータ文字列
     */
    const formatUserData = (user) => {
      const { user_name, additional_items } = user
      return [
        user_name, // ユーザー名
        ...additional_items.map((item) => {
          const type = item.item_type.split(':')[0]
          const composable = ITEM_TYPE_COMPOSABLE_MAP.get(type)
          const params = {
            ...Object.fromEntries(
              Object.entries(item).map(([key, value]) => [
                StringUtil.snakeToCamel(key),
                value,
              ])
            ),
            baseDate,
          }
          switch (type) {
            case ITEM_TYPE.POSITIONING:
            case ITEM_TYPE.REGISTRATION: {
              // 日時と経過時間で2列に分ける
              const { formattedDate, diffText } = composable(params)
              return [formattedDate.value, diffText.value]
            }
            case ITEM_TYPE.ACCURACY: {
              const { accuracyText } = composable(params)
              return accuracyText.value
            }
            case ITEM_TYPE.ACCURACY_RATING: {
              const { ratingText } = composable(params)
              return ratingText.value
            }
            case ITEM_TYPE.DEVICE:
            case ITEM_TYPE.SENSOR_NAMES: {
              // 専用のcomposableが無い項目。カードと同じくユーザー属性の部品を流用して
              // 値だけを出す(device は端末名、sensor_names はカンマ連結したセンサー名。
              // 紐付けが無い場合は null となり "-" になる)
              const { valueText } = useUserStatusAdditionalItemUserAttribute({
                value:
                  type === ITEM_TYPE.DEVICE ? params.deviceName : params.names,
                attributeType: null,
              })
              return valueText.value
            }
            default: {
              const { text } = composable(params)
              return text.value
            }
          }
        }), // 追加表示項目
      ]
        .flat()
        .join('\t')
    }

    /**
     * コピー用ユーザーデータ文字列
     * @type {ComputedRef.<string>}
     */
    const usersToCopy = computed(() =>
      users.value.map((user) => formatUserData(user)).join('\n')
    )

    return { usersToCopy }
  },
  computed: {
    /**
     * コピー件数の上限超過フラグ
     * @type {Boolean}
     */
    overMaxCopyCount: function () {
      return this.users.length > this.maxCopyCount
    },
  },
  methods: {
    /**
     * クリップボードへ書き込み
     */
    $_clipboardCopyClick: function () {
      if (this.overMaxCopyCount) {
        this.$_showOverMaxCopyCountAlert()
        return
      }
      navigator.clipboard
        .writeText(this.usersToCopy)
        .then(() => {
          this.$_showCopyCompletedToast()
        })
        .catch((error) => this.$_showCopyFailedAlert(error))
    },
    /**
     * コピー完了トースト表示
     */
    $_showCopyCompletedToast: function () {
      this.$toast.fire({
        position: 'top',
        icon: 'success',
        title: this.$t(
          'appMemberUserMenuItemClipboardCopy.message.copiedToClipboard'
        ),
        showConfirmButton: true,
        confirmButtonColor: '#6c757d',
        timer: 2500,
      })
    },
    /**
     * コピー件数の上限超過アラート表示
     */
    $_showOverMaxCopyCountAlert: function () {
      this.$_showErrorAlert(
        this.$t(
          'appMemberUserMenuItemClipboardCopy.message.exceededMaxCopyItemCount'
        ),
        this.$t(
          'appMemberUserMenuItemClipboardCopy.message.narrowDownCopyItemCount',
          { maxCount: this.maxCopyCount.toLocaleString() }
        )
      )
    },
    /**
     * コピー失敗アラート表示
     * @param {Error} error
     */
    $_showCopyFailedAlert: function (error) {
      let message = ''
      // アクセス権限エラーの場合はその旨をユーザーに表示
      if (error.name === 'NotAllowedError') {
        message = this.$t(
          'appMemberUserMenuItemClipboardCopy.message.noPermissionToAccessClipboard'
        )
        message += this.$i18n.locale === 'ja' ? '' : ' ' // 日本語でない場合はスペース
      }
      // 共通メッセージ
      message +=
        this.$t('appMemberUserMenuItemClipboardCopy.message.contactSupport') +
        `<br />${error.message}`

      this.$_showErrorAlert(
        this.$t('appMemberUserMenuItemClipboardCopy.message.failedToCopy'),
        message
      )
    },
    /**
     * エラーアラート表示
     * @param {String} title タイトル
     * @param {String} message エラーメッセージ
     */
    $_showErrorAlert: function (title, message) {
      return this.$alert.fire({
        icon: 'error',
        title,
        html: message,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.member-user-menu-item-clipboard-copy {
  .item {
    display: block;
    height: 30px;
    padding: 8px 16px;
    color: $text-color;
    font-size: $font-size-middle;
    line-height: 1;
    text-decoration: none;
    border-bottom: 1px solid #e8e8e8;
    background-color: #ffffff;
    user-select: none;

    &:hover {
      background-color: #f6f6f6;
      cursor: pointer;
    }
  }
}
</style>
